// frontend/src/pages/superadmin/DetectionsViewer.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Plane, Car, Shield, Wifi, WifiOff,
  RefreshCw, Trash2, Eye, X,
  Activity, Camera, Clock, CheckCircle,
  Search, Zap, DoorOpen, AlertTriangle,
  Image, Printer, Download, ChevronRight
} from 'lucide-react';

const API_BASE      = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const POLL_INTERVAL = 6000;
const EVENTS_IMG    = `${API_BASE}/uploads/agent_detections/events`;
const TEMP_IMG      = `${API_BASE}/uploads/agent_detections/temp`;

// ─── API helper ───────────────────────────────────────────────
const apiFetch = async (path, opts = {}) => {
  const token = localStorage.getItem('access_token');
  try {
    const resp = await axios({
      method: opts.method || 'GET',
      url: `${API_BASE}/surveillance${path}`,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...opts.headers,
      },
      ...(opts.body ? { data: opts.body } : {}),
    });
    return resp.data;
  } catch (err) {
    if (err.response?.status === 401) window.location.href = '/login';
    throw new Error(err.response?.status || err.message);
  }
};

// ─── CSV Export ───────────────────────────────────────────────
const exportCSV = (rows, filename) => {
  if (!rows?.length) return;
  const keys = Object.keys(rows[0]);
  const csv  = [
    keys.join(','),
    ...rows.map(r => keys.map(k => `"${(r[k] ?? '').toString().replace(/"/g,'""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

// ─── Helpers visuels ──────────────────────────────────────────
const fmt = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-US', {
    month:'short', day:'numeric',
    hour:'2-digit', minute:'2-digit', second:'2-digit',
  });
};

const fmtDur = (s) => {
  if (s == null) return null;
  if (s < 60)   return `${s}s`;
  if (s < 3600) return `${Math.floor(s/60)}m ${s%60}s`;
  return `${Math.floor(s/3600)}h ${Math.floor((s%3600)/60)}m`;
};

const accentOf = (type) => type === 'aircraft' ? 'var(--blue)' : 'var(--purple)';

// ─── Micro composants ─────────────────────────────────────────
const ConfBar = ({ value }) => {
  const pct = Math.round((value || 0) * 100);
  const c   = pct >= 80 ? 'var(--green)' : pct >= 60 ? 'var(--yellow)' : 'var(--red)';
  return (
    <div style={{ display:'flex', alignItems:'center', gap:5 }}>
      <div style={{ width:44, height:3, background:'var(--bg-700)', borderRadius:2, overflow:'hidden' }}>
        <div style={{ width:`${pct}%`, height:'100%', background:c }} />
      </div>
      <span style={{ fontSize:10, color:c, fontFamily:'var(--mono)' }}>{pct}%</span>
    </div>
  );
};

const Badge = ({ label, color }) => (
  <span style={{
    color, background:`${color}18`, border:`1px solid ${color}33`,
    padding:'2px 7px', borderRadius:4,
    fontSize:10, fontWeight:600, letterSpacing:'0.03em', fontFamily:'var(--mono)',
  }}>{label}</span>
);

const GateBadge = ({ action }) => {
  if (!action) return null;
  const map = {
    opened:          { label:'🔓 Opened',   color:'var(--green)' },
    denied:          { label:'🔒 Denied',   color:'var(--red)'   },
    manual_override: { label:'🔧 Override', color:'var(--yellow)'},
  };
  const cfg = map[action] || { label:action, color:'var(--text-muted)' };
  return <Badge label={cfg.label} color={cfg.color} />;
};

const EntityIcon = ({ type, size=14 }) => {
  const s = { width:size, height:size };
  if (type === 'aircraft') return <Plane style={{ ...s, color:'var(--blue)'   }} />;
  if (type === 'vehicle')  return <Car   style={{ ...s, color:'var(--purple)' }} />;
  return <Activity style={{ ...s, color:'var(--text-muted)' }} />;
};

const Btn = ({ onClick, children, color='var(--text-muted)', border, bg, disabled, style={} }) => (
  <button onClick={onClick} disabled={disabled} style={{
    display:'flex', alignItems:'center', gap:5,
    padding:'6px 12px', borderRadius:8, cursor: disabled ? 'not-allowed' : 'pointer',
    border: border || '1px solid var(--border)',
    background: bg || 'transparent',
    color, fontSize:11, fontFamily:'var(--font)', opacity: disabled ? 0.5 : 1,
    transition:'all 0.2s', ...style,
  }}>{children}</button>
);

const KpiCard = ({ label, value, sub, icon:Icon, color='var(--blue)', pulse=false }) => (
  <div style={{
    background:'var(--bg-800)', border:`1px solid ${color}25`,
    borderRadius:12, padding:'14px 18px',
    display:'flex', flexDirection:'column', gap:4, position:'relative',
  }}>
    {pulse && <span style={{
      position:'absolute', top:10, right:10, width:7, height:7,
      borderRadius:'50%', background:color, boxShadow:`0 0 8px ${color}`,
      animation:'blink 1.5s infinite',
    }}/>}
    <div style={{ display:'flex', alignItems:'center', gap:7 }}>
      {Icon && <Icon style={{ width:13, height:13, color }}/>}
      <span style={{ fontSize:10, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.06em' }}>{label}</span>
    </div>
    <span style={{ fontSize:22, fontWeight:700, color, lineHeight:1.1, fontFamily:'var(--mono)' }}>{value ?? '—'}</span>
    {sub && <span style={{ fontSize:10, color:'var(--text-dim)' }}>{sub}</span>}
  </div>
);

// ─── Field helper pour les cartes ─────────────────────────────
const Field = ({ label, value, mono=false }) => {
  if (!value && value !== 0) return null;
  return (
    <div style={{ marginBottom:8 }}>
      <div style={{ fontSize:10, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:2 }}>{label}</div>
      <div style={{ fontSize:13, color:'var(--text-body)', fontFamily: mono ? 'var(--mono)' : 'var(--font)' }}>{value}</div>
    </div>
  );
};

const CardSection = ({ label, color='var(--text-dim)', children }) => (
  <div style={{ marginBottom:16 }}>
    <div style={{
      fontSize:10, color, textTransform:'uppercase', letterSpacing:'0.07em',
      marginBottom:8, paddingBottom:4, borderBottom:'1px solid var(--border)',
    }}>{label}</div>
    {children}
  </div>
);

// ─── Lightbox Photo ───────────────────────────────────────────
const Lightbox = ({ src, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position:'fixed', inset:0, zIndex:2000,
      background:'rgba(0,0,0,0.92)', backdropFilter:'blur(6px)',
      display:'flex', alignItems:'center', justifyContent:'center',
    }}
  >
    <button onClick={onClose} style={{
      position:'absolute', top:20, right:20,
      background:'rgba(255,255,255,0.1)', border:'none',
      color:'white', cursor:'pointer', padding:8, borderRadius:8,
    }}>
      <X style={{ width:20, height:20 }}/>
    </button>
    <img
      src={src} alt="detection"
      onClick={e => e.stopPropagation()}
      style={{ maxWidth:'90vw', maxHeight:'90vh', borderRadius:8, objectFit:'contain' }}
      onError={e => { e.target.src=''; e.target.alt='Image unavailable'; }}
    />
  </div>
);

// ─── Event Card Modal (Presence Events) ──────────────────────
const EventCard = ({ eventId, onClose, onDelete }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    apiFetch(`/presence-events/${eventId}`)
      .then(setData).catch(console.error).finally(() => setLoading(false));
  }, [eventId]);

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cet événement et sa photo ?')) return;
    setDeleting(true);
    try {
      await apiFetch(`/presence-events/${eventId}`, { method:'DELETE' });
      onDelete(eventId); onClose();
    } catch (e) { alert(`Erreur: ${e.message}`); setDeleting(false); }
  };

  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const w = window.open('', '_blank');
    w.document.write(`
      <html><head><title>Event — ${data?.entity_id}</title>
      <style>
        body { font-family: 'DM Sans', sans-serif; background:#fff; color:#1e293b; padding:32px; }
        .print-card { max-width:600px; margin:0 auto; }
        .label { font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:#64748b; margin-bottom:2px; }
        .value { font-size:13px; color:#1e293b; margin-bottom:10px; }
        .section-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.07em;
          color:#475569; border-bottom:1px solid #e2e8f0; padding-bottom:4px; margin:16px 0 10px; }
        img { max-width:100%; border-radius:6px; margin-bottom:16px; }
        h1 { font-size:20px; font-weight:700; margin-bottom:4px; }
        .meta { font-size:12px; color:#64748b; margin-bottom:20px; }
        @media print { body { padding:16px; } }
      </style></head>
      <body>${content}</body></html>
    `);
    w.document.close();
    w.focus(); w.print(); w.close();
  };

  const isAircraft = data?.entity_type === 'aircraft';
  const ac = data ? accentOf(data.entity_type) : 'var(--blue)';

  return (
    <>
      <div onClick={onClose} style={{
        position:'fixed', inset:0, zIndex:1000,
        background:'rgba(0,0,0,0.8)', backdropFilter:'blur(4px)',
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          background:'var(--bg-900)', border:'1px solid var(--border)',
          borderRadius:16, width:'min(780px,95vw)', maxHeight:'90vh',
          display:'flex', flexDirection:'column', overflow:'hidden',
          boxShadow:'0 32px 80px rgba(0,0,0,0.6)',
        }}>

          {/* Header carte */}
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'14px 18px', borderBottom:'1px solid var(--border)',
            background:'var(--bg-800)',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <EntityIcon type={data?.entity_type} size={18}/>
              <span style={{ fontSize:16, fontWeight:700, color:ac, fontFamily:'var(--mono)', letterSpacing:'0.04em' }}>
                {data?.entity_id || '…'}
              </span>
              {data?.zone && <Badge label={data.zone.toUpperCase()} color='var(--text-muted)'/>}
              {data?.entity_type && <Badge label={data.entity_type.toUpperCase()} color={ac}/>}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn onClick={handlePrint} color='var(--cyan)' border='1px solid rgba(6,182,212,0.3)' bg='rgba(6,182,212,0.08)'>
                <Printer style={{ width:12, height:12 }}/> Print
              </Btn>
              <Btn onClick={handleDelete} disabled={deleting} color='var(--red)' border='1px solid rgba(239,68,68,0.3)' bg='rgba(239,68,68,0.08)'>
                <Trash2 style={{ width:12, height:12 }}/>{deleting ? 'Deleting…' : 'Delete'}
              </Btn>
              <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-dim)', cursor:'pointer', padding:4 }}>
                <X style={{ width:18, height:18 }}/>
              </button>
            </div>
          </div>

          {loading ? (
            <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)' }}>Loading…</div>
          ) : (
            <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

              {/* Photo */}
              <div style={{
                flex:'0 0 340px', background:'#000',
                display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
                borderRight:'1px solid var(--border)', gap:12, padding:12,
              }}>
                {data?.best_image_path ? (
                  <>
                    <img
                      src={`${EVENTS_IMG}/${data.best_image_path}`}
                      alt="best frame"
                      style={{ width:'100%', borderRadius:8, objectFit:'contain', maxHeight:280, cursor:'zoom-in' }}
                      onClick={() => setLightbox(true)}
                      onError={e => { e.target.style.display='none'; }}
                    />
                    <Btn onClick={() => setLightbox(true)} color='var(--text-muted)' style={{ fontSize:10 }}>
                      <Eye style={{ width:11, height:11 }}/> View full size
                    </Btn>
                  </>
                ) : (
                  <div style={{ color:'var(--text-dim)', display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
                    <Image style={{ width:32, height:32 }}/>
                    <span style={{ fontSize:12 }}>No image</span>
                  </div>
                )}
              </div>

              {/* Infos printables */}
              <div ref={printRef} style={{ flex:1, overflowY:'auto', padding:'16px 20px' }}>

                {/* Print header (visible uniquement à l'impression) */}
                <div className="print-only" style={{ display:'none' }}>
                  <h1>{data?.entity_id}</h1>
                  <div className="meta">{data?.entity_type?.toUpperCase()} · {fmt(data?.entry_time)}</div>
                  {data?.best_image_path && (
                    <img src={`${EVENTS_IMG}/${data.best_image_path}`} alt="frame" style={{ maxHeight:200 }}/>
                  )}
                </div>

                <CardSection label="Event">
                  <Field label="Entry"      value={fmt(data?.entry_time)} />
                  <Field label="Exit"       value={fmt(data?.exit_time)} />
                  <Field label="Duration"   value={fmtDur(data?.duration_s)} />
                  <Field label="Zone"       value={data?.zone} />
                  <Field label="Camera"     value={data?.camera_id} />
                  <Field label="Frames"     value={data?.frame_count} mono />
                  <Field label="Confidence" value={data?.confidence ? `${Math.round(data.confidence*100)}%` : null} mono />
                </CardSection>

                {data?.owner_matched ? (
                  <CardSection label="Owner" color='var(--green)'>
                    <Field label="Name"  value={data?.owner_name} />
                    <Field label="Email" value={data?.owner_email} />
                    <Field label="Phone" value={data?.owner_phone} />
                  </CardSection>
                ) : (
                  <div style={{
                    background:'rgba(249,115,22,0.08)', border:'1px solid rgba(249,115,22,0.2)',
                    borderRadius:8, padding:'10px 12px', marginBottom:14,
                    display:'flex', alignItems:'center', gap:8, fontSize:11, color:'var(--orange)',
                  }}>
                    <AlertTriangle style={{ width:13, height:13 }}/> Owner not matched
                  </div>
                )}

                {isAircraft ? (
                  <CardSection label="Aircraft">
                    <Field label="Tail"         value={data?.entity_id} mono />
                    <Field label="Type"         value={data?.aircraft_type} />
                    <Field label="Manufacturer" value={data?.manufacturer} />
                    <Field label="Model"        value={data?.aircraft_model} />
                    <Field label="Engine"       value={data?.engine_type} />
                    <Field label="Seats"        value={data?.seats} />
                  </CardSection>
                ) : (data?.veh_make || data?.veh_vin) && (
                  <CardSection label="Vehicle">
                    <Field label="Plate" value={data?.entity_id} mono />
                    <Field label="Make"  value={data?.veh_make} />
                    <Field label="Model" value={data?.veh_model} />
                    <Field label="Year"  value={data?.veh_year} />
                    <Field label="Color" value={data?.veh_color} />
                    <Field label="Type"  value={data?.vehicle_type} />
                    <Field label="VIN"   value={data?.veh_vin} mono />
                  </CardSection>
                )}

                {data?.gate_events?.length > 0 && (
                  <CardSection label="Gate History" color='var(--green)'>
                    {data.gate_events.map(ge => (
                      <div key={ge.id} style={{
                        display:'flex', alignItems:'center', justifyContent:'space-between',
                        background:'var(--bg-800)', borderRadius:6, padding:'8px 10px', marginBottom:6,
                      }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <GateBadge action={ge.action}/>
                          <span style={{ fontSize:10, color:'var(--text-dim)', fontFamily:'var(--mono)' }}>Gate {ge.gate_id}</span>
                        </div>
                        <div style={{ textAlign:'right' }}>
                          <div style={{ fontSize:10, color:'var(--text-dim)' }}>{fmt(ge.triggered_at)}</div>
                          <div style={{ fontSize:10, color:'var(--bg-600)' }}>by {ge.opened_by}</div>
                        </div>
                      </div>
                    ))}
                  </CardSection>
                )}

              </div>
            </div>
          )}
        </div>
      </div>
      {lightbox && data?.best_image_path && (
        <Lightbox src={`${EVENTS_IMG}/${data.best_image_path}`} onClose={() => setLightbox(false)}/>
      )}
    </>
  );
};

// ─── Live Frame Card Modal ─────────────────────────────────────
const FrameCard = ({ det, onClose, onDelete }) => {
  const [lightbox, setLightbox] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const printRef = useRef();

  const isAircraft = det.entity_type === 'aircraft';
  const ac = accentOf(det.entity_type);
  const imgSrc = det.image_path ? `${TEMP_IMG}/${det.image_path}` : null;

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer ce frame ${det.tail_number || det.plate_number} ?`)) return;
    setDeleting(true);
    try {
      await apiFetch(`/detections/${det.id}`, { method:'DELETE' });
      onDelete(det.id); onClose();
    } catch (e) { alert(`Erreur: ${e.message}`); setDeleting(false); }
  };

  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const w = window.open('', '_blank');
    w.document.write(`
      <html><head><title>Frame — ${det.tail_number || det.plate_number}</title>
      <style>
        body { font-family:'DM Sans',sans-serif; background:#fff; color:#1e293b; padding:32px; }
        .label { font-size:10px; text-transform:uppercase; letter-spacing:.05em; color:#64748b; margin-bottom:2px; }
        .value { font-size:13px; color:#1e293b; margin-bottom:10px; }
        .section-title { font-size:11px; font-weight:700; text-transform:uppercase;
          color:#475569; border-bottom:1px solid #e2e8f0; padding-bottom:4px; margin:16px 0 10px; }
        img { max-width:100%; border-radius:6px; margin-bottom:16px; }
        h1 { font-size:20px; font-weight:700; margin-bottom:4px; }
        @media print { body { padding:16px; } }
      </style></head>
      <body>${content}</body></html>
    `);
    w.document.close(); w.focus(); w.print(); w.close();
  };

  return (
    <>
      <div onClick={onClose} style={{
        position:'fixed', inset:0, zIndex:1000,
        background:'rgba(0,0,0,0.8)', backdropFilter:'blur(4px)',
        display:'flex', alignItems:'center', justifyContent:'center',
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          background:'var(--bg-900)', border:'1px solid var(--border)',
          borderRadius:16, width:'min(680px,95vw)', maxHeight:'88vh',
          display:'flex', flexDirection:'column', overflow:'hidden',
          boxShadow:'0 32px 80px rgba(0,0,0,0.6)',
        }}>

          {/* Header */}
          <div style={{
            display:'flex', alignItems:'center', justifyContent:'space-between',
            padding:'14px 18px', borderBottom:'1px solid var(--border)',
            background:'var(--bg-800)',
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <EntityIcon type={det.entity_type} size={18}/>
              <span style={{ fontSize:16, fontWeight:700, color:ac, fontFamily:'var(--mono)', letterSpacing:'0.04em' }}>
                {det.tail_number || det.plate_number || 'UNKNOWN'}
              </span>
              <Badge label="LIVE FRAME" color='var(--text-dim)'/>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <Btn onClick={handlePrint} color='var(--cyan)' border='1px solid rgba(6,182,212,0.3)' bg='rgba(6,182,212,0.08)'>
                <Printer style={{ width:12, height:12 }}/> Print
              </Btn>
              <Btn onClick={handleDelete} disabled={deleting} color='var(--red)' border='1px solid rgba(239,68,68,0.3)' bg='rgba(239,68,68,0.08)'>
                <Trash2 style={{ width:12, height:12 }}/>{deleting ? 'Deleting…' : 'Delete'}
              </Btn>
              <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-dim)', cursor:'pointer', padding:4 }}>
                <X style={{ width:18, height:18 }}/>
              </button>
            </div>
          </div>

          <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

            {/* Photo */}
            <div style={{
              flex:'0 0 300px', background:'#000',
              display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
              borderRight:'1px solid var(--border)', gap:10, padding:12,
            }}>
              {imgSrc ? (
                <>
                  <img
                    src={imgSrc} alt="frame"
                    style={{ width:'100%', borderRadius:8, objectFit:'contain', maxHeight:240, cursor:'zoom-in' }}
                    onClick={() => setLightbox(true)}
                    onError={e => { e.target.style.display='none'; }}
                  />
                  <Btn onClick={() => setLightbox(true)} color='var(--text-muted)' style={{ fontSize:10 }}>
                    <Eye style={{ width:11, height:11 }}/> View full size
                  </Btn>
                </>
              ) : (
                <div style={{ color:'var(--text-dim)', display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
                  <Image style={{ width:28, height:28 }}/>
                  <span style={{ fontSize:11 }}>No image</span>
                </div>
              )}
            </div>

            {/* Infos */}
            <div ref={printRef} style={{ flex:1, overflowY:'auto', padding:'16px 18px' }}>

              <div style={{ display:'none' }}>
                <h1>{det.tail_number || det.plate_number}</h1>
              </div>

              <CardSection label="Detection Frame">
                <Field label="Timestamp"  value={fmt(det.timestamp)} />
                <Field label="Camera"     value={det.camera_name} />
                <Field label="Event type" value={det.event_type} mono />
                <Field label="Confidence" value={`${Math.round((det.confidence||0)*100)}%`} mono />
                <Field label="Zone"       value={det.zone} />
              </CardSection>

              {isAircraft && (
                <CardSection label="ADS-B">
                  {det.adsb_matched ? (
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
                      <Wifi style={{ width:12, height:12, color:'var(--cyan)' }}/>
                      <span style={{ fontSize:12, color:'var(--cyan)', fontWeight:600 }}>
                        {det.adsb_movement_type} confirmed
                      </span>
                    </div>
                  ) : (
                    <div style={{ display:'flex', alignItems:'center', gap:6, color:'var(--text-dim)', fontSize:11 }}>
                      <WifiOff style={{ width:11, height:11 }}/> No ADS-B match yet
                    </div>
                  )}
                  {det.aircraft_type && <Field label="Type" value={det.aircraft_type}/>}
                </CardSection>
              )}

              {det.owner_matched && det.owner_name && (
                <CardSection label="Owner" color='var(--green)'>
                  <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
                    <CheckCircle style={{ width:12, height:12, color:'var(--green)' }}/>
                    <span style={{ fontSize:13, color:'var(--green)', fontWeight:600 }}>{det.owner_name}</span>
                  </div>
                  <Field label="Email" value={det.owner_email}/>
                </CardSection>
              )}
              {!det.owner_matched && (
                <div style={{
                  background:'rgba(249,115,22,0.08)', border:'1px solid rgba(249,115,22,0.2)',
                  borderRadius:8, padding:'10px 12px', marginBottom:14,
                  display:'flex', alignItems:'center', gap:8, fontSize:11, color:'var(--orange)',
                }}>
                  <AlertTriangle style={{ width:12, height:12 }}/> Owner not matched
                </div>
              )}

              {!isAircraft && (det.veh_make || det.veh_model) && (
                <CardSection label="Vehicle">
                  <Field label="Make"  value={det.veh_make}/>
                  <Field label="Model" value={det.veh_model}/>
                </CardSection>
              )}

              {det.gate_authorized && (
                <div style={{
                  background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.2)',
                  borderRadius:8, padding:'10px 12px',
                  display:'flex', alignItems:'center', gap:8,
                }}>
                  <Shield style={{ width:13, height:13, color:'var(--green)' }}/>
                  <span style={{ fontSize:12, color:'var(--green)', fontWeight:600 }}>Gate Authorized</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {lightbox && imgSrc && <Lightbox src={imgSrc} onClose={() => setLightbox(false)}/>}
    </>
  );
};

// ─── Presence Event Row ───────────────────────────────────────
const PresenceRow = ({ ev, isNew, onSelect, onDelete }) => {
  const [hovered, setHovered] = useState(false);
  const ac = accentOf(ev.entity_type);

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer l'événement ${ev.entity_id} ?`)) return;
    try {
      await apiFetch(`/presence-events/${ev.id}`, { method:'DELETE' });
      onDelete(ev.id);
    } catch (err) { alert(`Erreur: ${err.message}`); }
  };

  return (
    <div
      onClick={() => onSelect(ev.id)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:'grid',
        gridTemplateColumns:'18px 1fr 90px 70px 90px 70px 120px 32px',
        gap:'0 10px', alignItems:'center',
        padding:'9px 14px', borderBottom:'1px solid var(--bg-800)',
        borderLeft: isNew ? `2px solid ${ac}` : '2px solid transparent',
        background: hovered ? 'rgba(30,41,59,0.5)' : isNew ? `${ac}08` : 'transparent',
        cursor:'pointer', transition:'background 0.2s',
      }}
    >
      <EntityIcon type={ev.entity_type} size={12}/>
      <div>
        <span style={{ fontFamily:'var(--mono)', fontSize:13, fontWeight:700, color:ac, letterSpacing:'0.04em' }}>
          {ev.entity_id || '???'}
        </span>
        {ev.owner_name && <span style={{ fontSize:11, color:'var(--text-dim)', marginLeft:8 }}>{ev.owner_name}</span>}
        <div style={{ fontSize:10, color:'var(--bg-600)', marginTop:1 }}>
          {ev.veh_make ? `${ev.veh_make} ${ev.veh_model||''} ${ev.veh_year||''}` :
           ev.aircraft_type ? `${ev.manufacturer||''} ${ev.aircraft_type}` : ''}
          {ev.veh_vin && <span style={{ marginLeft:6 }}>VIN: {ev.veh_vin}</span>}
        </div>
      </div>
      <span style={{ fontSize:10, color:'var(--text-dim)', display:'flex', alignItems:'center', gap:4 }}>
        <Camera style={{ width:9, height:9 }}/>{ev.camera_id || '—'}
      </span>
      <ConfBar value={ev.confidence}/>
      <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
        {ev.gate_triggered && <GateBadge action={ev.last_gate_action || 'opened'}/>}
        {ev.owner_matched && !ev.gate_triggered && (
          <span style={{ fontSize:10, color:'var(--green)', display:'flex', alignItems:'center', gap:3 }}>
            <CheckCircle style={{ width:9, height:9 }}/> Owner ✓
          </span>
        )}
        {!ev.owner_matched && <span style={{ fontSize:10, color:'var(--orange)' }}>Unknown</span>}
      </div>
      <span style={{ fontSize:10, color:'var(--text-dim)' }}>{fmtDur(ev.duration_s) || '—'}</span>
      <span style={{ fontSize:10, color:'var(--text-dim)', fontFamily:'var(--mono)' }}>{fmt(ev.entry_time)}</span>
      <button onClick={handleDelete} style={{
        background:'none', border:'none', cursor:'pointer',
        color: hovered ? 'var(--red)' : 'transparent',
        padding:4, borderRadius:4, transition:'color 0.2s',
        display:'flex', alignItems:'center',
      }}>
        <Trash2 style={{ width:12, height:12 }}/>
      </button>
    </div>
  );
};

// ─── Live Frame Row ───────────────────────────────────────────
const LiveRow = ({ det, isNew, onSelect }) => {
  const [hovered, setHovered] = useState(false);
  const ac = accentOf(det.entity_type);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display:'grid',
        gridTemplateColumns:'18px 1fr 100px 80px 120px 34px 34px 120px',
        gap:'0 10px', alignItems:'center',
        padding:'9px 14px', borderBottom:'1px solid var(--bg-800)',
        borderLeft: isNew ? `2px solid ${ac}` : '2px solid transparent',
        background: hovered ? 'rgba(30,41,59,0.5)' : isNew ? `${ac}08` : 'transparent',
        transition:'background 0.2s',
      }}
    >
      <EntityIcon type={det.entity_type} size={12}/>
      <div>
        <span style={{ fontFamily:'var(--mono)', fontSize:13, fontWeight:600, color:ac, letterSpacing:'0.04em' }}>
          {det.tail_number || det.plate_number || '???'}
        </span>
        {det.owner_name && <span style={{ fontSize:11, color:'var(--text-dim)', marginLeft:8 }}>{det.owner_name}</span>}
      </div>
      <span style={{ fontSize:11, color:'var(--text-dim)', display:'flex', alignItems:'center', gap:4 }}>
        <Camera style={{ width:10, height:10 }}/>{det.camera_name || '—'}
      </span>
      <ConfBar value={det.confidence}/>
      <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
        {det.adsb_matched && (
          <span style={{ fontSize:10, color:'var(--cyan)', display:'flex', alignItems:'center', gap:3 }}>
            <Wifi style={{ width:9, height:9 }}/> ADS-B
          </span>
        )}
        {det.gate_authorized && (
          <span style={{ fontSize:10, color:'var(--green)', display:'flex', alignItems:'center', gap:3 }}>
            <Shield style={{ width:9, height:9 }}/> Gate ✓
          </span>
        )}
        {det.owner_matched && !det.gate_authorized && (
          <span style={{ fontSize:10, color:'var(--green)', display:'flex', alignItems:'center', gap:3 }}>
            <CheckCircle style={{ width:9, height:9 }}/> Owner ✓
          </span>
        )}
      </div>

      {/* Bouton photo */}
      <button
        onClick={() => onSelect(det)}
        title="View frame details"
        style={{
          background:'none', border:'none', cursor:'pointer',
          color: hovered ? 'var(--blue)' : 'var(--text-dim)',
          padding:4, borderRadius:4, transition:'color 0.2s',
          display:'flex', alignItems:'center',
        }}
      >
        <Eye style={{ width:13, height:13 }}/>
      </button>

      {/* Bouton supprimer */}
      <button
        onClick={async (e) => {
          e.stopPropagation();
          if (!window.confirm(`Supprimer ce frame ${det.tail_number || det.plate_number} ?`)) return;
          try {
            await apiFetch(`/detections/${det.id}`, { method:'DELETE' });
            onSelect(null); // signal suppression
          } catch (err) { alert(`Erreur: ${err.message}`); }
        }}
        title="Delete frame"
        style={{
          background:'none', border:'none', cursor:'pointer',
          color: hovered ? 'var(--red)' : 'transparent',
          padding:4, borderRadius:4, transition:'color 0.2s',
          display:'flex', alignItems:'center',
        }}
      >
        <Trash2 style={{ width:12, height:12 }}/>
      </button>

      <span style={{ fontSize:10, color:'var(--text-dim)', fontFamily:'var(--mono)' }}>{fmt(det.timestamp)}</span>
    </div>
  );
};

// ─── Gate Access Tab ──────────────────────────────────────────
const GateAccessTab = ({ onExport }) => {
  const [gateId, setGateId]     = useState('');
  const [events, setEvents]     = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [override, setOverride] = useState({ open:false, gateId:'', entityId:'' });

  const fetchGateEvents = async () => {
    if (!gateId.trim()) return;
    setLoading(true);
    try {
      const d = await apiFetch(`/gates/${gateId}/events?limit=100`);
      setEvents(d.events || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const fetchSessions = useCallback(async () => {
    try {
      const d = await apiFetch('/gates/sessions/active');
      setSessions(d.sessions || []);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchSessions();
    const t = setInterval(fetchSessions, 5000);
    return () => clearInterval(t);
  }, [fetchSessions]);

  const handleOverride = async () => {
    try {
      await apiFetch(`/gates/${override.gateId}/override`, {
        method:'POST',
        body:{ entity_id:override.entityId, action:'opened', notes:'Manual override from dashboard' },
      });
      setOverride({ open:false, gateId:'', entityId:'' });
      fetchGateEvents();
    } catch (e) { alert(`Erreur: ${e.message}`); }
  };

  return (
    <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:16 }}>

      {/* Sessions actives */}
      <div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
          <div style={{ fontSize:11, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.06em', display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background:'var(--green)', display:'inline-block', boxShadow:'0 0 6px var(--green)', animation:'blink 2s infinite' }}/>
            Active Sessions ({sessions.length})
          </div>
          {sessions.length > 0 && (
            <Btn onClick={() => exportCSV(sessions, 'active_sessions.csv')} color='var(--text-dim)' style={{ fontSize:10, padding:'4px 8px' }}>
              <Download style={{ width:10, height:10 }}/> CSV
            </Btn>
          )}
        </div>
        {sessions.length === 0 ? (
          <div style={{ fontSize:12, color:'var(--text-dim)', padding:'12px 0' }}>No active sessions</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
            {sessions.map(s => (
              <div key={s.session_id} style={{
                display:'grid', gridTemplateColumns:'18px 1fr 80px 60px 60px 100px',
                gap:'0 10px', alignItems:'center',
                background:'var(--bg-800)', border:`1px solid ${accentOf(s.entity_type)}20`,
                borderRadius:8, padding:'8px 12px',
              }}>
                <EntityIcon type={s.entity_type} size={12}/>
                <span style={{ fontFamily:'var(--mono)', fontSize:13, fontWeight:700, color:accentOf(s.entity_type) }}>{s.entity_id}</span>
                <span style={{ fontSize:10, color:'var(--text-dim)' }}><Camera style={{ width:9, height:9 }}/> {s.camera_id}</span>
                <span style={{ fontSize:10, color:'var(--text-muted)' }}>{s.frame_count} frames</span>
                <span style={{ fontSize:10, color: s.owner_matched ? 'var(--green)':'var(--orange)' }}>
                  {s.owner_matched ? '✓ Owner':'Unknown'}
                </span>
                <span style={{ fontSize:10, color:'var(--text-dim)' }}>{fmt(s.first_seen)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Gate history */}
      <div>
        <div style={{ fontSize:11, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8 }}>Gate History</div>
        <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
          <input
            value={gateId} onChange={e => setGateId(e.target.value)}
            placeholder="Gate ID…"
            onKeyDown={e => e.key === 'Enter' && fetchGateEvents()}
            style={{
              background:'var(--bg-800)', border:'1px solid var(--border)', borderRadius:8,
              padding:'7px 10px', color:'var(--text-white)', fontSize:12, fontFamily:'var(--font)',
              outline:'none', width:180,
            }}
          />
          <Btn onClick={fetchGateEvents} color='var(--text-muted)'>{loading ? 'Loading…':'Search'}</Btn>
          <Btn onClick={() => setOverride({ open:true, gateId, entityId:'' })} color='var(--green)' border='1px solid rgba(34,197,94,0.3)' bg='rgba(34,197,94,0.08)'>
            <DoorOpen style={{ width:12, height:12 }}/> Manual Override
          </Btn>
          {events.length > 0 && (
            <Btn onClick={() => exportCSV(events, `gate_${gateId}_events.csv`)} color='var(--text-dim)' style={{ fontSize:10 }}>
              <Download style={{ width:10, height:10 }}/> CSV
            </Btn>
          )}
        </div>

        {events.length > 0 && (
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <div style={{
              display:'grid', gridTemplateColumns:'120px 1fr 90px 80px 120px',
              gap:'0 10px', padding:'5px 10px',
              fontSize:10, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em',
              borderBottom:'1px solid var(--border)',
            }}>
              <span>Action</span><span>Entity</span><span>By</span><span>Conf.</span><span>Time</span>
            </div>
            {events.map(ge => (
              <div key={ge.id} style={{
                display:'grid', gridTemplateColumns:'120px 1fr 90px 80px 120px',
                gap:'0 10px', alignItems:'center',
                background:'var(--bg-800)', borderRadius:6, padding:'8px 10px',
              }}>
                <GateBadge action={ge.action}/>
                <span style={{ fontFamily:'var(--mono)', fontSize:12, color:'var(--text-muted)' }}>{ge.entity_id}</span>
                <span style={{ fontSize:11, color:'var(--text-dim)' }}>{ge.opened_by}</span>
                <ConfBar value={ge.confidence_at_trigger}/>
                <span style={{ fontSize:10, color:'var(--text-dim)' }}>{fmt(ge.triggered_at)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Override modal */}
      {override.open && (
        <div onClick={() => setOverride(o => ({...o, open:false}))} style={{
          position:'fixed', inset:0, zIndex:1000,
          background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background:'var(--bg-900)', border:'1px solid var(--border)',
            borderRadius:12, padding:24, width:340,
          }}>
            <div style={{ fontSize:14, fontWeight:700, color:'var(--text-white)', marginBottom:16 }}>Manual Gate Override</div>
            {[['GATE ID', 'gateId'], ['ENTITY ID (plate or tail)', 'entityId']].map(([lbl, key]) => (
              <div key={key} style={{ marginBottom:12 }}>
                <div style={{ fontSize:10, color:'var(--text-dim)', marginBottom:4 }}>{lbl}</div>
                <input value={override[key]} onChange={e => setOverride(o => ({...o,[key]:e.target.value}))}
                  style={{ width:'100%', background:'var(--bg-800)', border:'1px solid var(--border)', borderRadius:8, padding:'7px 10px', color:'var(--text-white)', fontSize:12, boxSizing:'border-box' }}/>
              </div>
            ))}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn onClick={() => setOverride(o => ({...o, open:false}))} color='var(--text-dim)'>Cancel</Btn>
              <Btn onClick={handleOverride} color='var(--green)' border='1px solid rgba(34,197,94,0.4)' bg='rgba(34,197,94,0.1)'>
                <DoorOpen style={{ width:12, height:12 }}/> Open Gate
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────
const DetectionsViewer = () => {
  const [stats, setStats]             = useState(null);
  const [liveFeed, setLiveFeed]       = useState([]);
  const [presenceEvents, setPresence] = useState([]);
  const [vehicleLookups, setVehicles] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);   // presence event id
  const [selectedFrame, setSelectedFrame] = useState(null);   // live frame object
  const [loading, setLoading]         = useState(true);
  const [liveOn, setLiveOn]           = useState(true);
  const [processing, setProcessing]   = useState(false);
  const [activeTab, setActiveTab]     = useState('presence');
  const [filter, setFilter]           = useState({ entity:'', owner_matched:'' });
  const [search, setSearch]           = useState('');
  const [newIds, setNewIds]           = useState(new Set());
  const [presencePage, setPresencePage] = useState({ total:0, offset:0, limit:50 });
  const prevLiveIds = useRef(new Set());
  const pollRef     = useRef(null);

  // ── Fetchers ────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    try { setStats(await apiFetch('/detections/stats?days=7')); } catch(e) {}
  }, []);

  const fetchLive = useCallback(async () => {
    try {
      const data = await apiFetch('/detections/live?limit=60');
      const incoming = Array.isArray(data) ? data : [];
      const freshIds = new Set();
      incoming.forEach(d => { if (!prevLiveIds.current.has(d.id)) freshIds.add(d.id); });
      prevLiveIds.current = new Set(incoming.map(d => d.id));
      setNewIds(freshIds);
      setLiveFeed(incoming);
      if (freshIds.size > 0) setTimeout(() => setNewIds(new Set()), 4000);
    } catch(e) {}
  }, []);

  const fetchPresence = useCallback(async (offset=0) => {
    try {
      const params = new URLSearchParams({ limit:50, offset });
      if (filter.entity) params.set('entity_type', filter.entity);
      if (filter.owner_matched !== '') params.set('owner_matched', filter.owner_matched);
      const data = await apiFetch(`/presence-events?${params}`);
      setPresence(data.events || []);
      setPresencePage({ total:data.total, offset, limit:data.limit });
    } catch(e) {}
  }, [filter]);

  const fetchVehicles = useCallback(async () => {
    try {
      const data = await apiFetch('/vehicle-lookups?limit=100');
      setVehicles(Array.isArray(data) ? data : []);
    } catch(e) {}
  }, []);

  // ── Init & polling ───────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchLive(), fetchPresence()]);
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (liveOn) pollRef.current = setInterval(() => { fetchLive(); fetchStats(); }, POLL_INTERVAL);
    return () => clearInterval(pollRef.current);
  }, [liveOn, fetchLive, fetchStats]);

  useEffect(() => {
    if (activeTab === 'presence') fetchPresence(0);
    if (activeTab === 'vehicles') fetchVehicles();
  }, [activeTab, filter]);

  useEffect(() => {
    const t = setTimeout(() => { if (activeTab === 'presence') fetchPresence(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const handleProcess = async () => {
    setProcessing(true);
    try {
      const res = await apiFetch('/process', { method:'POST' });
      await Promise.all([fetchLive(), fetchStats(), fetchPresence(0)]);
      alert(`✅ Processed\n• Aircraft: ${res.stats?.aircraft ?? 0}\n• Vehicles: ${res.stats?.vehicles ?? 0}\n• Errors: ${res.stats?.errors ?? 0}`);
    } catch(e) { alert(`❌ ${e.message}`); } finally { setProcessing(false); }
  };

  const filteredLive = liveFeed.filter(d => {
    if (filter.entity && d.entity_type !== filter.entity) return false;
    if (search) {
      const q = search.toLowerCase();
      return (d.tail_number||'').toLowerCase().includes(q)
          || (d.plate_number||'').toLowerCase().includes(q)
          || (d.camera_name||'').toLowerCase().includes(q)
          || (d.owner_name||'').toLowerCase().includes(q);
    }
    return true;
  });

  const t = stats?.totals || {};

  const TABS = [
    { id:'presence', label:'Presence Events', count:presencePage.total },
    { id:'live',     label:'Live Feed',       count:filteredLive.length },
    { id:'gate',     label:'Gate Access',     count:null },
    { id:'vehicles', label:'Vehicle Lookups', count:vehicleLookups.length },
  ];

  return (
    <div style={{
      display:'flex', flexDirection:'column', height:'100%',
      color:'var(--text-white)',
      fontFamily:'var(--font)', fontSize:13,
      background:'var(--bg-900)',
    }}>
      <style>{`
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
        ::-webkit-scrollbar{width:5px;height:5px}
        ::-webkit-scrollbar-track{background:var(--bg-800)}
        ::-webkit-scrollbar-thumb{background:var(--bg-700);border-radius:3px}
        @media print { .no-print{display:none!important} .print-only{display:block!important} }
      `}</style>

      {/* Header */}
      <div style={{
        padding:'13px 20px', borderBottom:'1px solid var(--border)',
        display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0,
        background:'var(--bg-800)',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{
            width:8, height:8, borderRadius:'50%',
            background: liveOn ? 'var(--green)' : 'var(--text-dim)',
            boxShadow: liveOn ? '0 0 8px var(--green)' : 'none',
            animation: liveOn ? 'blink 2s infinite' : 'none',
          }}/>
          <div>
            <h2 style={{ fontSize:15, fontWeight:700, color:'var(--text-white)', margin:0, letterSpacing:'-0.2px' }}>
              Detection Stream
            </h2>
            <p style={{ fontSize:10, color:'var(--text-dim)', margin:0 }}>
              Visual surveillance · Session processing · Gate control
            </p>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <Btn onClick={() => setLiveOn(v => !v)}
            color={liveOn ? 'var(--green)' : 'var(--text-dim)'}
            border={`1px solid ${liveOn ? 'rgba(34,197,94,0.3)' : 'var(--border)'}`}
            bg={liveOn ? 'rgba(34,197,94,0.08)' : 'transparent'}>
            {liveOn ? <Wifi style={{width:12,height:12}}/> : <WifiOff style={{width:12,height:12}}/>}
            {liveOn ? 'Live' : 'Paused'}
          </Btn>
          <Btn onClick={handleProcess} disabled={processing}
            color='var(--yellow)' border='1px solid rgba(234,179,8,0.3)' bg='rgba(234,179,8,0.08)'>
            <Zap style={{width:12,height:12}}/>{processing ? 'Processing…':'Process Now'}
          </Btn>
          {/* Export global */}
          <Btn onClick={() => {
            if (activeTab==='presence') exportCSV(presenceEvents, 'presence_events.csv');
            else if (activeTab==='live') exportCSV(filteredLive, 'live_feed.csv');
            else if (activeTab==='vehicles') exportCSV(vehicleLookups, 'vehicle_lookups.csv');
          }} color='var(--cyan)' border='1px solid rgba(6,182,212,0.3)' bg='rgba(6,182,212,0.08)'>
            <Download style={{width:12,height:12}}/> Export CSV
          </Btn>
          <button onClick={() => { fetchLive(); fetchStats(); fetchPresence(presencePage.offset); }}
            style={{ padding:'6px 10px', borderRadius:8, cursor:'pointer', border:'1px solid var(--border)', background:'transparent', color:'var(--text-dim)' }}>
            <RefreshCw style={{width:13,height:13}}/>
          </button>
        </div>
      </div>

      {/* KPIs */}
      {stats && (
        <div style={{
          display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',
          gap:10, padding:'14px 20px', borderBottom:'1px solid var(--border)', flexShrink:0,
        }}>
          <KpiCard label="Total (7d)"     value={t.total}         icon={Activity} color='var(--blue)'   pulse={liveOn}/>
          <KpiCard label="Aircraft"       value={t.aircraft_count} icon={Plane}    color='var(--blue)'/>
          <KpiCard label="Vehicles"       value={t.vehicle_count}  icon={Car}      color='var(--purple)'/>
          <KpiCard label="ADS-B Match"    value={t.adsb_confirmed} icon={Wifi}     color='var(--cyan)'/>
          <KpiCard label="Gates Opened"   value={t.gates_opened}   icon={DoorOpen} color='var(--green)'/>
          <KpiCard label="Pending"        value={t.pending}        icon={Clock}    color='var(--yellow)' sub={t.pending > 0 ? 'needs processing':'all clear'}/>
          <KpiCard label="Avg Confidence" value={t.avg_confidence ? `${t.avg_confidence}%`:'—'} icon={Eye} color='var(--orange)'/>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid var(--border)', padding:'0 20px', flexShrink:0, background:'var(--bg-800)' }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding:'10px 16px', background:'none', border:'none', cursor:'pointer',
            fontSize:12, fontFamily:'var(--font)',
            color: activeTab===tab.id ? 'var(--text-white)' : 'var(--text-dim)',
            borderBottom: activeTab===tab.id ? '2px solid var(--blue)' : '2px solid transparent',
            display:'flex', alignItems:'center', gap:6, transition:'color 0.2s',
          }}>
            {tab.label}
            {tab.count != null && tab.count > 0 && (
              <span style={{
                fontSize:10, padding:'1px 6px', borderRadius:10,
                background: activeTab===tab.id ? 'rgba(59,130,246,0.2)' : 'var(--bg-700)',
                color: activeTab===tab.id ? 'var(--blue)' : 'var(--text-dim)',
              }}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Filtres */}
      {(activeTab==='presence' || activeTab==='live') && (
        <div style={{
          display:'flex', gap:8, padding:'10px 20px',
          borderBottom:'1px solid var(--bg-800)', flexShrink:0, alignItems:'center',
          flexWrap:'wrap',
        }}>
          <div style={{ position:'relative', flex:1, maxWidth:260 }}>
            <Search style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', width:12, height:12, color:'var(--text-dim)' }}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search tail, plate, camera, owner…"
              style={{
                width:'100%', background:'var(--bg-800)', border:'1px solid var(--border)',
                borderRadius:8, padding:'6px 8px 6px 28px', color:'var(--text-white)',
                fontSize:11, fontFamily:'var(--font)', outline:'none', boxSizing:'border-box',
              }}
            />
          </div>
          {['','aircraft','vehicle'].map(type => (
            <button key={type} onClick={() => setFilter(f => ({...f, entity:type}))} style={{
              padding:'5px 10px', borderRadius:6, fontSize:11, cursor:'pointer', fontFamily:'var(--font)',
              border:`1px solid ${filter.entity===type ? 'var(--border-accent)':'var(--border)'}`,
              background: filter.entity===type ? 'var(--blue-glow)' : 'transparent',
              color: filter.entity===type ? 'var(--blue)' : 'var(--text-dim)',
            }}>
              {type==='' ? 'All' : type==='aircraft' ? '✈ Aircraft' : '🚗 Vehicle'}
            </button>
          ))}
          {activeTab==='presence' && (
            [['','All'],['true','Owner ✓'],['false','Unknown']].map(([v,l]) => (
              <button key={v} onClick={() => setFilter(f => ({...f, owner_matched:v}))} style={{
                padding:'5px 10px', borderRadius:6, fontSize:11, cursor:'pointer', fontFamily:'var(--font)',
                border:`1px solid ${filter.owner_matched===v ? 'rgba(34,197,94,0.3)':'var(--border)'}`,
                background: filter.owner_matched===v ? 'rgba(34,197,94,0.08)' : 'transparent',
                color: filter.owner_matched===v ? 'var(--green)' : 'var(--text-dim)',
              }}>{l}</button>
            ))
          )}
          <span style={{ fontSize:10, color:'var(--text-dim)', marginLeft:'auto' }}>
            {activeTab==='presence' ? `${presencePage.total} events` : `${filteredLive.length} / ${liveFeed.length} frames`}
          </span>
        </div>
      )}

      {/* Tab content */}
      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

        {/* ── Presence Events ── */}
        {activeTab==='presence' && (
          <div style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column' }}>
            <div style={{
              display:'grid',
              gridTemplateColumns:'18px 1fr 90px 70px 90px 70px 120px 32px',
              gap:'0 10px', padding:'7px 14px', borderBottom:'1px solid var(--border)',
              fontSize:10, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.06em',
              position:'sticky', top:0, background:'rgba(8,13,26,0.97)', zIndex:1,
            }}>
              <span/><span>Identifier / Owner</span><span>Camera</span>
              <span>Conf.</span><span>Gate</span><span>Duration</span>
              <span>Entry</span><span/>
            </div>
            {loading ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>Loading…</div>
            ) : presenceEvents.length===0 ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>No presence events</div>
            ) : (
              presenceEvents.map(ev => (
                <PresenceRow key={ev.id} ev={ev} isNew={newIds.has(ev.id)}
                  onSelect={setSelectedEvent}
                  onDelete={id => setPresence(prev => prev.filter(e => e.id !== id))}
                />
              ))
            )}
            {presencePage.total > presencePage.limit && (
              <div style={{ display:'flex', gap:8, padding:'12px 20px', borderTop:'1px solid var(--border)', justifyContent:'center', flexShrink:0 }}>
                <Btn disabled={presencePage.offset===0}
                  onClick={() => fetchPresence(Math.max(0, presencePage.offset-presencePage.limit))}
                  color='var(--text-dim)' style={{ opacity: presencePage.offset===0 ? 0.4:1 }}>← Prev</Btn>
                <span style={{ fontSize:11, color:'var(--text-dim)', padding:'5px 8px' }}>
                  {presencePage.offset+1}–{Math.min(presencePage.offset+presencePage.limit, presencePage.total)} / {presencePage.total}
                </span>
                <Btn disabled={presencePage.offset+presencePage.limit>=presencePage.total}
                  onClick={() => fetchPresence(presencePage.offset+presencePage.limit)}
                  color='var(--text-dim)' style={{ opacity: presencePage.offset+presencePage.limit>=presencePage.total ? 0.4:1 }}>Next →</Btn>
              </div>
            )}
          </div>
        )}

        {/* ── Live Feed ── */}
        {activeTab==='live' && (
          <div style={{ flex:1, overflowY:'auto' }}>
            <div style={{
              display:'grid',
              gridTemplateColumns:'18px 1fr 100px 80px 120px 34px 34px 120px',
              gap:'0 10px', padding:'7px 14px', borderBottom:'1px solid var(--border)',
              fontSize:10, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.06em',
              position:'sticky', top:0, background:'rgba(8,13,26,0.97)', zIndex:1,
            }}>
              <span/><span>Identifier / Owner</span><span>Camera</span>
              <span>Confidence</span><span>Status</span>
              <span title="View"><Eye style={{ width:10, height:10 }}/></span>
              <span title="Delete"><Trash2 style={{ width:10, height:10 }}/></span>
              <span>Time</span>
            </div>
            {loading ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>Loading…</div>
            ) : filteredLive.length===0 ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>No detections</div>
            ) : (
              filteredLive.map(det => (
                <LiveRow key={det.id} det={det} isNew={newIds.has(det.id)}
                  onSelect={(d) => {
                    if (d === null) {
                      setLiveFeed(prev => prev.filter(x => x.id !== det.id));
                    } else {
                      setSelectedFrame(d);
                    }
                  }}
                />
              ))
            )}
          </div>
        )}

        {/* ── Gate Access ── */}
        {activeTab==='gate' && <GateAccessTab/>}

        {/* ── Vehicle Lookups ── */}
        {activeTab==='vehicles' && (
          <div style={{ flex:1, overflowY:'auto', padding:'12px 20px' }}>
            {vehicleLookups.length===0 ? (
              <div style={{ padding:40, textAlign:'center', color:'var(--text-dim)' }}>No vehicle lookups yet</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                <div style={{
                  display:'grid', gridTemplateColumns:'110px 80px 80px 55px 70px 80px 160px 1fr 70px',
                  gap:'0 12px', padding:'6px 10px',
                  fontSize:10, color:'var(--text-dim)', textTransform:'uppercase', letterSpacing:'0.05em',
                  borderBottom:'1px solid var(--border)',
                }}>
                  <span>Plate</span><span>Make</span><span>Model</span><span>Year</span>
                  <span>Color</span><span>Type</span><span>VIN</span><span>Owner</span><span>API</span>
                </div>
                {vehicleLookups.map(v => (
                  <div key={v.id} style={{
                    display:'grid', gridTemplateColumns:'110px 80px 80px 55px 70px 80px 160px 1fr 70px',
                    gap:'0 12px', padding:'8px 10px',
                    background:'var(--bg-800)', borderRadius:8, border:'1px solid var(--border)',
                    fontSize:12, alignItems:'center',
                  }}>
                    <span style={{ fontFamily:'var(--mono)', color:'var(--purple)', fontWeight:700 }}>{v.plate_number}</span>
                    <span style={{ color:'var(--text-body)' }}>{v.make||'—'}</span>
                    <span style={{ color:'var(--text-body)' }}>{v.model||'—'}</span>
                    <span style={{ color:'var(--text-body)' }}>{v.year||'—'}</span>
                    <span style={{ color:'var(--text-body)' }}>{v.color||'—'}</span>
                    <span style={{ color:'var(--text-body)' }}>{v.vehicle_type||'—'}</span>
                    <span style={{ color:'var(--text-dim)', fontFamily:'var(--mono)', fontSize:10 }}>{v.vin||'—'}</span>
                    <span style={{ color: v.owner_name ? 'var(--green)':'var(--text-dim)' }}>{v.owner_name||'Unknown'}</span>
                    <span style={{ fontSize:10, color:'var(--text-dim)', background:'var(--bg-700)', padding:'2px 6px', borderRadius:4 }}>{v.api_provider||'—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{
        padding:'6px 20px', borderTop:'1px solid var(--border)',
        display:'flex', alignItems:'center', gap:16,
        fontSize:10, color:'var(--text-dim)', flexShrink:0, background:'var(--bg-800)',
      }}>
        <span style={{ display:'flex', alignItems:'center', gap:4 }}>
          <span style={{ width:6, height:6, borderRadius:'50%', background:liveOn ? 'var(--green)':'var(--text-dim)', display:'inline-block' }}/>
          {liveOn ? `Polling every ${POLL_INTERVAL/1000}s` : 'Polling paused'}
        </span>
        {stats && <>
          <span>·</span><span>{t.total||0} total (7d)</span>
          <span>·</span><span>{t.gates_opened||0} gates opened</span>
        </>}
        <span style={{ marginLeft:'auto' }}>{new Date().toLocaleTimeString()}</span>
      </div>

      {/* Modals */}
      {selectedEvent && (
        <EventCard
          eventId={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onDelete={id => { setPresence(prev => prev.filter(e => e.id !== id)); setSelectedEvent(null); }}
        />
      )}
      {selectedFrame && (
        <FrameCard
          det={selectedFrame}
          onClose={() => setSelectedFrame(null)}
          onDelete={id => { setLiveFeed(prev => prev.filter(x => x.id !== id)); setSelectedFrame(null); }}
        />
      )}
    </div>
  );
};

export default DetectionsViewer;